/**
 * scanIntegrity — a scanner-quality tripwire.
 *
 * A species that is not released in Pokémon GO cannot appear in the player's
 * in-game Pokédex, so the live scanner can never legitimately see it. If a scan
 * nonetheless tries to write a "caught" for an unreleased species, that is not a
 * catch — it is a strong signal the scanner MIS-MAPPED a cell to the wrong
 * National Dex id (the 2026-07-26 Galar false-writes: 12 unreleased Gen-8
 * species written caught in one scan burst).
 *
 * The write path (dexStateStore.applyScanObservation[s]) rejects those writes so
 * they can never inflate the dex above the released roster. This module is the
 * FLAG half: it records each rejection and logs it loudly so the problem is
 * visible in the exportable scan log / device console instead of silently
 * corrupting data. The counter is per scan session (reset in useDexLiveScan.start).
 */

export interface RejectedUnreleasedWrite {
  /** The variant the scan tried to write. */
  variant: string;
  /** National Dex id the scan tried to mark present. */
  dexNumber: number;
  /** ms epoch of the rejection. */
  at: number;
}

let rejected: RejectedUnreleasedWrite[] = [];

/**
 * Record — and loudly log — a scan write that was rejected because the species
 * is not released in Pokémon GO. Called from the dex-store write guard.
 */
export function recordRejectedUnreleasedScanWrite(variant: string, dexNumber: number): void {
  rejected.push({ variant, dexNumber, at: Date.now() });
  // Loud + specific, and captured by the exportable scan log (console tee), so a
  // device trace shows exactly which cells the scanner mis-read.
  // eslint-disable-next-line no-console
  console.warn(
    `[dex-scan] REJECTED unreleased write, ${variant} #${dexNumber} is not released in ` +
      `Pokémon GO. A scan can only see released species, so this is almost certainly a ` +
      `scanner mis-map (wrong dex id for a cell). Write dropped.`,
  );
}

/** Rejections recorded since the last reset (this scan session). */
export function getRejectedUnreleasedWrites(): readonly RejectedUnreleasedWrite[] {
  return rejected;
}

/** Count of rejections this session — for the scan diagnostics / summary. */
export function getRejectedUnreleasedCount(): number {
  return rejected.length;
}

/** Clear the log — call at the start of a scan session so the count is per-run. */
export function resetRejectedUnreleasedWrites(): void {
  rejected = [];
}
